// Načítanie obsadenosti z verejného Google Kalendára.
// Web z kalendára iba číta – obsadené dni sa zobrazia ako nedostupné.
//
// Zdroje (v poradí):
//   1. Google Calendar API v3 – spoľahlivé, funguje priamo z prehliadača,
//      vyžaduje config.googleApiKey (návod v src/config.js)
//   2. Priamy iCal feed – funguje mimo prehliadača (testy, Node)
//   3. iCal feed cez verejné CORS proxy – núdzová záloha

import config from '../config';
import { toKey, fromKey, addDays } from './dates';

const CACHE_KEY = 'ck_ical_cache_v1';

/* ----------------------- Google Calendar API ----------------------- */

/** Zablokuje všetky dni od `from` po `to` (vrátane oboch). */
function blockDays(occupied, from, to) {
  let cursor = new Date(from);
  cursor.setHours(0, 0, 0, 0);
  const last = new Date(to);
  last.setHours(0, 0, 0, 0);
  while (cursor <= last) {
    occupied.add(toKey(cursor));
    cursor = addDays(cursor, 1);
  }
}

/**
 * Udalosti z Calendar API -> množina obsadených dní.
 * Blokujú len celodenné a viacdňové udalosti – krátka časová udalosť
 * v rámci jedného dňa (napr. hodinová schôdzka) dostupnosť neovplyvní.
 */
export function apiEventsToOccupiedSet(items) {
  const occupied = new Set();
  for (const item of items || []) {
    if (!item.start) continue;

    if (item.start.date) {
      // Celodenná udalosť: end.date je exkluzívny (deň PO poslednom dni).
      const start = fromKey(item.start.date);
      const endExclusive =
        item.end && item.end.date ? fromKey(item.end.date) : addDays(start, 1);
      blockDays(occupied, start, addDays(endExclusive, -1));
    } else if (item.start.dateTime) {
      const start = new Date(item.start.dateTime);
      const end = new Date(item.end && item.end.dateTime ? item.end.dateTime : item.start.dateTime);
      // Posledný reálne obsadený moment (koniec o polnoci nezasahuje do ďalšieho dňa).
      const lastMoment = new Date(Math.max(start.getTime(), end.getTime() - 1));
      if (toKey(start) === toKey(lastMoment)) continue; // jednodňová časová udalosť – ignoruj
      blockDays(occupied, start, lastMoment);
    }
  }
  return occupied;
}

async function fetchFromGoogleApi() {
  const timeMin = new Date();
  timeMin.setDate(timeMin.getDate() - 1);
  const timeMax = new Date();
  timeMax.setMonth(timeMax.getMonth() + config.calendarMonthsAhead + 1);

  const params = new URLSearchParams({
    key: config.googleApiKey,
    singleEvents: 'true', // rozbalí aj opakujúce sa udalosti
    maxResults: '2500',
    timeMin: timeMin.toISOString(),
    timeMax: timeMax.toISOString(),
    fields: 'items(start,end)',
  });
  const url = `https://www.googleapis.com/calendar/v3/calendars/${encodeURIComponent(
    config.googleCalendarId
  )}/events?${params}`;

  const response = await fetch(url);
  if (!response.ok) throw new Error(`Calendar API HTTP ${response.status}`);
  const data = await response.json();
  return [...apiEventsToOccupiedSet(data.items)];
}

/* ----------------------------- iCal feed ---------------------------- */

/** Rozbalí riadky iCal (pokračovacie riadky začínajú medzerou alebo tabulátorom). */
function unfoldLines(text) {
  return text
    .replace(/\r\n/g, '\n')
    .replace(/\n[ \t]/g, '')
    .split('\n');
}

/** 'DTSTART;VALUE=DATE:20260815' alebo 'DTSTART:20260815T100000Z' -> Date (lokálny deň). */
function parseIcalDate(value) {
  const match = value.match(/(\d{4})(\d{2})(\d{2})(?:T(\d{2})(\d{2}))?/);
  if (!match) return null;
  const [, y, m, d, hh, mm] = match;
  return {
    date: new Date(Number(y), Number(m) - 1, Number(d), Number(hh || 0), Number(mm || 0)),
    isAllDay: !hh,
  };
}

/**
 * Vytiahne z iCal textu zoznam udalostí { start, end, isAllDay }.
 * DTEND je pri celodenných udalostiach exkluzívny (deň PO poslednom dni).
 * Pozn.: opakujúce sa udalosti (RRULE) tento jednoduchý parser nerozbaľuje –
 * Calendar API (zdroj č. 1) ich rozbaľuje automaticky.
 */
export function parseIcal(text) {
  const lines = unfoldLines(text);
  const events = [];
  let current = null;

  for (const line of lines) {
    if (line.startsWith('BEGIN:VEVENT')) {
      current = {};
    } else if (line.startsWith('END:VEVENT')) {
      if (current && current.start) {
        if (!current.end) current.end = { ...current.start };
        events.push(current);
      }
      current = null;
    } else if (current) {
      if (line.startsWith('DTSTART')) {
        current.start = parseIcalDate(line.split(':').pop());
      } else if (line.startsWith('DTEND')) {
        current.end = parseIcalDate(line.split(':').pop());
      }
    }
  }
  return events;
}

/**
 * Prevedie iCal udalosti na množinu obsadených dní (Set kľúčov 'YYYY-MM-DD').
 * Rovnaké pravidlo ako pri API: blokujú len celodenné a viacdňové udalosti,
 * krátke časové udalosti v rámci jedného dňa sa ignorujú.
 */
export function eventsToOccupiedSet(events) {
  const occupied = new Set();
  for (const event of events) {
    if (!event.start) continue;
    const start = event.start.date;
    const end = (event.end || event.start).date;

    if (event.start.isAllDay) {
      // DTEND je exkluzívny (deň PO poslednom dni); bez DTEND ide o jeden deň.
      const endExclusive = end > start ? end : addDays(start, 1);
      blockDays(occupied, start, addDays(endExclusive, -1));
    } else {
      const lastMoment = new Date(Math.max(start.getTime(), end.getTime() - 1));
      if (toKey(start) === toKey(lastMoment)) continue; // jednodňová časová udalosť – ignoruj
      blockDays(occupied, start, lastMoment);
    }
  }
  return occupied;
}

/** Načíta iCal – najprv priamo, pri zlyhaní (CORS) skúša proxy po jednom. */
async function fetchIcalText() {
  const attempts = [
    config.icalUrl,
    ...config.corsProxies.map((proxy) => proxy + encodeURIComponent(config.icalUrl)),
  ];
  let lastError;
  for (const url of attempts) {
    try {
      const response = await fetch(url, { cache: 'no-store' });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const text = await response.text();
      if (text.includes('BEGIN:VCALENDAR')) return text;
      throw new Error('Odpoveď nie je iCal');
    } catch (error) {
      lastError = error;
    }
  }
  throw lastError;
}

/* ------------------------------ Verejné ------------------------------ */

/**
 * Vráti pole obsadených dní. Používa krátku cache v sessionStorage,
 * aby sa dáta nesťahovali pri každom prekreslení.
 */
export async function loadOccupiedDays({ force = false } = {}) {
  if (!force) {
    try {
      const cached = JSON.parse(sessionStorage.getItem(CACHE_KEY));
      if (cached && Date.now() - cached.time < config.icalCacheMinutes * 60000) {
        return cached.days;
      }
    } catch (error) {
      /* poškodená cache – ignoruj */
    }
  }

  let days;
  if (config.googleApiKey) {
    try {
      days = await fetchFromGoogleApi();
    } catch (error) {
      /* API zlyhalo – skúsime iCal nižšie */
    }
  }
  if (!days) {
    const text = await fetchIcalText();
    days = [...eventsToOccupiedSet(parseIcal(text))];
  }

  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({ time: Date.now(), days }));
  } catch (error) {
    /* plné úložisko – ignoruj */
  }
  return days;
}
